import * as React from 'react';
import { Checkbox, Space, Typography, Tag, Collapse, Slider, InputNumber, Row, Col, Button, Radio, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import {
    tagFiltersAtom,
    creatorTagsAtomById,
    youtubeFollowersRangeAtom,
    instagramFollowersRangeAtom,
    tiktokFollowersRangeAtom,
    pageAtom,
    tagCountsAtom,
    hasContactFilterAtom,
    HasContactFilter,
} from './atoms';
import { generate } from '@ant-design/colors';
import { getPaletteColor, YT_ICON, IG_ICON, TT_ICON } from './utils';
import { RightOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;

export const TagFilterSidebar: React.FC = () => {
    const [tagFilters, setTagFilters] = useAtom(tagFiltersAtom);
    const [{ data: tagOptions }] = useAtom(creatorTagsAtomById(0));
    const [{ data: tagCounts }] = useAtom(tagCountsAtom);
    const [youtubeRange, setYoutubeRangeAtom] = useAtom(youtubeFollowersRangeAtom);
    const [instagramRange, setInstagramRangeAtom] = useAtom(instagramFollowersRangeAtom);
    const [tiktokRange, setTiktokRangeAtom] = useAtom(tiktokFollowersRangeAtom);
    const [hasContact, setHasContact] = useAtom(hasContactFilterAtom);
    const [, setPage] = useAtom(pageAtom);

    const MAX_FOLLOWERS = 1000000; // 1M+

    // Helper function to get count for a tag value
    const getTagCount = (tagValue: string): number => {
        if (!tagCounts) return 0;
        // Extract tag_category_value_id from the value string (format: "categoryId,valueId")
        const parts = tagValue.split(',');
        if (parts.length < 2) return 0;
        const valueId = parseInt(parts[1], 10);
        const tagCountObj = tagCounts.find(tc => tc.tag_category_value_id === valueId);
        return tagCountObj ? tagCountObj.count : 0;
    };

    // Filter and transform tag options to show only the first 3 priority categories
    const priorityTagOptions = React.useMemo(() => {
        if (!tagOptions) return [];

        const priorityCategories = ['Prime Channel Category', 'Content Category', 'Channel Type'];

        // Rename "Specific" to "Content Category" and filter
        const filtered = tagOptions
            .map((option) => ({
                ...option,
                label: option.label === 'Specific' ? 'Content Category' : option.label,
            }))
            .filter(option => priorityCategories.includes(option.label))
            .sort((a, b) => {
                const aIndex = priorityCategories.indexOf(a.label);
                const bIndex = priorityCategories.indexOf(b.label);
                return aIndex - bIndex;
            });
        
        return filtered;
    }, [tagOptions]);

    const handleCheckboxChange = (categoryValue: string, checked: boolean) => {
        if (checked) {
            setTagFilters([...tagFilters, categoryValue]);
        } else {
            setTagFilters(tagFilters.filter(value => value !== categoryValue));
        }
        // Whenever tags change, reset to first page so results are in range
        setPage(1);
    };

    const handleClearAll = () => {
        setTagFilters([]);
        setYoutubeRangeAtom([0, MAX_FOLLOWERS]);
        setInstagramRangeAtom([0, MAX_FOLLOWERS]);
        setTiktokRangeAtom([0, MAX_FOLLOWERS]);
        setHasContact('all');
        setPage(1);
    };

    const formatFollowerCount = (value: number): string => {
        if (value >= 1000000) return '1M+';
        // if (value >= 1000) return `${Math.floor(value / 1000)}K`;
        return value.toString();
    };

    const FollowerRangeFilter: React.FC<{
        icon: JSX.Element;
        platformName: string;
        range: [number, number];
        onChange: (value: [number, number]) => void;
    }> = ({ icon, platformName, range, onChange }) => {
        const [localMin, setLocalMin] = React.useState<number>(range[0]);
        const [localMax, setLocalMax] = React.useState<number>(range[1]);

        // Update local state when range prop changes (e.g., from Clear All)
        React.useEffect(() => {
            setLocalMin(range[0]);
            setLocalMax(range[1]);
        }, [range]);

        const applyChange = () => {
            onChange([localMin, localMax]);
        };

        const handleKeyPress = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                applyChange();
            }
        };

        return (
            <div
                style={{
                    padding: '12px 0',
                }}
            >
                <Space size={4} style={{ marginBottom: 8 }}>
                    {icon}
                    <Text strong style={{ fontSize: '13px' }}>{platformName}</Text>
                </Space>
                <Slider
                    range
                    min={0}
                    max={MAX_FOLLOWERS}
                    step={10000}
                    value={range}
                    onChange={onChange}
                    tooltip={{ formatter: formatFollowerCount }}
                    marks={{
                            0: '0',
                            250000: '250K',
                            500000: '500K',
                            750000: '750K',
                            1000000: '1M+',
                        }}
                />
                <Row gutter={8} style={{ marginTop: 8 }}>
                    <Col span={10}>
                        <InputNumber
                            size="small"
                            min={0}
                            max={MAX_FOLLOWERS}
                            value={localMin}
                            onChange={(val) => setLocalMin(val || 0)}
                            onKeyPress={handleKeyPress}
                            formatter={formatFollowerCount}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={10}>
                        <InputNumber
                            size="small"
                            min={0}
                            max={MAX_FOLLOWERS}
                            value={localMax}
                            onChange={(val) => setLocalMax(val || MAX_FOLLOWERS)}
                            onKeyPress={handleKeyPress}
                            formatter={formatFollowerCount}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={4}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<RightOutlined />}
                            onClick={applyChange}
                            style={{ width: '100%', borderRadius: '30px' }}
                        />
                    </Col>
                </Row>
            </div>
        );
    };

    const hasActiveFilters = () => {
        return tagFilters.length > 0
            || youtubeRange[0] !== 0 || youtubeRange[1] !== MAX_FOLLOWERS
            || instagramRange[0] !== 0 || instagramRange[1] !== MAX_FOLLOWERS
            || tiktokRange[0] !== 0 || tiktokRange[1] !== MAX_FOLLOWERS
            || hasContact !== 'all';
    };

    const getActiveFilterCount = () => {
        let count = tagFilters.length;
        if (youtubeRange[0] !== 0 || youtubeRange[1] !== MAX_FOLLOWERS) count++;
        if (instagramRange[0] !== 0 || instagramRange[1] !== MAX_FOLLOWERS) count++;
        if (tiktokRange[0] !== 0 || tiktokRange[1] !== MAX_FOLLOWERS) count++;
        if (hasContact !== 'all') count++;
        return count;
    };

    return (
        <div
            style={{
                width: '280px',
                padding: '0px',
                backgroundColor: 'var(--bg-light, #fafafa)',
                borderRight: '1px solid var(--border-light, #d9d9d9)',
                height: '100%',
                overflowY: 'auto',
                color: 'var(--text-dark, #000)',
            }}
        >
            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
                <div>
                    <Title level={5} style={{ marginBottom: '8px' }}>
                        Filters
                    </Title>
                    {hasActiveFilters() && (
                        <a
                            onClick={handleClearAll}
                            style={{ fontSize: '12px', color: '#1890ff' }}
                        >
                            Clear all filters ({getActiveFilterCount()})
                        </a>
                    )}
                </div>

                {/* Connection link filter */}
                <div style={{ padding: '12px', backgroundColor: 'var(--bg-light, #fafafa)', borderRadius: '4px', color: 'var(--text-dark, #000)' }}>
                    <Space direction='vertical' size={8} style={{ width: '100%' }}>
                        <Space size={4}>
                            <Title level={5} style={{ margin: 0, fontSize: '14px' }}>
                                Connection
                            </Title>
                            <Tooltip title='Show creators with or without a linked connection (manager, agent, etc.) from the Connections module.'>
                                <QuestionCircleOutlined style={{ color: '#999', fontSize: 13 }} />
                            </Tooltip>
                        </Space>
                        <Radio.Group
                            size='small'
                            value={hasContact}
                            onChange={(e) => {
                                setHasContact(e.target.value as HasContactFilter);
                                setPage(1);
                            }}
                            optionType='button'
                            buttonStyle='solid'
                            options={[
                                { label: 'Any', value: 'all' },
                                { label: 'Linked', value: 'has' },
                                { label: 'Missing', value: 'missing' },
                            ]}
                        />
                    </Space>
                </div>

                {/* Follower Range Filters */}
                <div style={{ padding: '12px', backgroundColor: 'var(--bg-light, #fafafa)', borderRadius: '4px', color: 'var(--text-dark, #000)' }}>
					<Title level={5} style={{ marginBottom: '12px', fontSize: '14px' }}>
                        Followers Range
                    </Title>
                    <FollowerRangeFilter
                        icon={YT_ICON}
						platformName='YouTube'
                        range={youtubeRange}
                        onChange={(value) => {
                            setYoutubeRangeAtom(value);
                            setPage(1);
                        }}
                    />
                    <FollowerRangeFilter
                        icon={IG_ICON}
						platformName='Instagram'
                        range={instagramRange}
                        onChange={(value) => {
                            setInstagramRangeAtom(value);
                            setPage(1);
                        }}
                    />
                    <FollowerRangeFilter
                        icon={TT_ICON}
						platformName='TikTok'
                        range={tiktokRange}
                        onChange={(value) => {
                            setTiktokRangeAtom(value);
                            setPage(1);
                        }}
                    />
                </div>

                <Collapse
                    defaultActiveKey={[]}
                    ghost
                    expandIconPosition='end'
                >
                    {priorityTagOptions.map((category) => (
                        <Panel
                            header={<strong>{category.label}</strong>}
                            key={category.value}
                        >
                            <Space direction='vertical' style={{ width: '100%' }} size='small'>
                                {category.children.map((child) => {
                                    const isChecked = tagFilters.includes(child.value);
                                    const palette = child.color ? generate(child.color) : [];
                                    const textColor = getPaletteColor(child.color, palette);
                                    const count = getTagCount(child.value);

                                    return (
                                        <Checkbox
                                    key={child.value}
                                    checked={isChecked}
                                    onChange={(e) => handleCheckboxChange(child.value, e.target.checked)}
                                    style={{ width: '100%' }}
                                >
                                    <Tag
                                        color={child.color}
                                        style={{
                                            color: textColor,
                                            marginLeft: '5px',
                                            border: 'none',
                                        }}
                                    >
                                                {child.label} ({count})
                                            </Tag>
                                        </Checkbox>
                                    );
                                })}
                            </Space>
                        </Panel>
                    ))}
                </Collapse>
            </Space>
        </div>
    );
};
